import { Panel } from "../../../components/panels/Panel";
import { DiorDataDashboardGeneralDataBlock } from "./components/DiorDataDashboardGeneralDataBlock";
import { DiorDataDashboardPreciousDataBlock } from "./components/DiorDataDashboardPreciousDataBlock";
import { DiorDataDashboardBathsMaintenance } from "./components/DiorDataDashboardBathsMaintenance";
import { DiorDataDashboardGraphRacksPerProcess } from "./components/DiorDataDashboardGraphRacksPerProcess";
import { patchData, useGetData } from "../../../services/api/api-tools";
import { SimpleTable } from "../../../components/tables/SimpleTable";
import { formatSecondsToHMS, formatSecondsToMinutesSeconds } from "../../../helpers/date/date-helper";
import { GroupButton } from "../../../components/buttons/GroupButton";
import { useEffect, useMemo, useState } from "react";
import {
  getCurrentDay,
  getCurrentMonth,
  getCurrentWeek, getDiorQuery,
  getLastRollingDays, getWorkedSecondsToday
} from "../../../services/dashboard/dior-dashboard-service";
import { getInfluxdbData } from "../../../services/api/influxdb-tools";
import {
  findPoolByRealId,
  getSchemeId,
  PRECIOUS_POOLS_INDEXES
} from "../../../services/live-interface/dior/dior-interface-service";
import { Loader } from "../../../components/loading/Loader";


export const DiorDataDashboard = () => {

  const { data: baths, isLoading: bathsAreLoading, refetch: refetchBaths } = useGetData('physical_units', 'physical_units');
  const { data: processes, isLoading: processesAreLoading, refetch: refetchProcesses } = useGetData('patterns', 'patterns');

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRangeIndex, setSelectedRangeIndex] = useState(0);
  const selectedRange = useMemo(() => {
    return [getCurrentDay(), getCurrentWeek(), getLastRollingDays(), getCurrentMonth()][selectedRangeIndex];
  }, [selectedRangeIndex]);
  const previousRange = {
    start: selectedRange.start - (selectedRange.stop - selectedRange.start),
    stop: selectedRange.start,
  };
  const workedSecondsToday = getWorkedSecondsToday(
    selectedRangeIndex === 0 ? null : selectedRange.start,
    selectedRangeIndex !== 2
  );
  const [data, setData] = useState({});

  useEffect(() => {
    if (!selectedRange || !processes) return;
    const start = Math.floor(selectedRange.start / 1000);
    const stop = Math.floor(selectedRange.stop / 1000);
    const previousStart = Math.floor(previousRange.start / 1000);
    const previousStop = Math.floor(previousRange.stop / 1000);

    const getData = async () => {
      setIsLoading(true);
      const data = {};

      await Promise.all(
        [
          { queryName: 'total_racks_processed', checkPrevious: true },
          { queryName: 'line_occupancy', checkPrevious: true },
          { queryName: 'racks_per_process', checkPrevious: false },
          { queryName: 'baths_mean', checkPrevious: false },
          { queryName: 'baths_maintenance', checkPrevious: false },
        ].map(async ({ queryName, checkPrevious }) => {
          let requestData;
          requestData = await getInfluxdbData(
            null, null, [], [], null, null, null, null, null, null, null,
            getDiorQuery(queryName, start, stop, processes.map((process) => process.name)),
          );
          data[queryName] = requestData;

          if (checkPrevious) {
            requestData = await getInfluxdbData(
              null, null, [], [], null, null, null, null, null, null, null,
              getDiorQuery(queryName, previousStart, previousStop, processes.map((process) => process.name)),
            );
            data[queryName + '_previous'] = requestData;
          }
      }));

      setData(data);
      setIsLoading(false);
    };

    getData();
  }, [selectedRange, processes]);

  const handleUpdateBath = async (physicalUnit) => {
    let path = `physical_units/${physicalUnit.id}`;

    let body = {
      data: physicalUnit.data,
    };

    return await patchData(path, body);
  };

  /* Data formatting */
  const totalRacksProcessed = data?.total_racks_processed?.[0]?.num_of_racks ?? '-';
  const totalRacksProcessedPrevious = data?.total_racks_processed_previous?.[0]?.num_of_racks ?? 0;

  const averageThroughput = Math.floor(totalRacksProcessed / (workedSecondsToday / 3600) * 10) / 10 ?? '-';
  const averageThroughputPrevious = Math.floor(totalRacksProcessedPrevious / (workedSecondsToday / 3600) * 10) / 10;

  const averageOccupancyPercent = Math.ceil(data['line_occupancy']?.reduce((sum, { total_duration_seconds }) => sum + total_duration_seconds, 0) / data['line_occupancy']?.length / workedSecondsToday * 100) ?? null;
  const preciousLineOccupancy = data['line_occupancy']?.filter((pool) => PRECIOUS_POOLS_INDEXES.includes(parseInt(pool.pool_id))) ?? [];
  const preciousOccupancyPercent = Math.ceil(preciousLineOccupancy?.reduce((sum, { total_duration_seconds }) => sum + total_duration_seconds, 0) / preciousLineOccupancy.length / workedSecondsToday * 100) ?? null;

  const averageOccupancyPercentPrevious = Math.ceil(data['line_occupancy_previous']?.reduce((sum, { total_duration_seconds }) => sum + total_duration_seconds, 0) / data['line_occupancy_previous']?.length / workedSecondsToday * 100) ?? null;
  const preciousLineOccupancyPrevious = data['line_occupancy_previous']?.filter((pool) => PRECIOUS_POOLS_INDEXES.includes(parseInt(pool.pool_id))) ?? [];
  const preciousOccupancyPercentPrevious = Math.ceil(preciousLineOccupancyPrevious?.reduce((sum, { total_duration_seconds }) => sum + total_duration_seconds, 0) / preciousLineOccupancyPrevious.length / workedSecondsToday * 100) ?? null;

  const allGoldMeanDuration = ((baths) => baths.reduce((sum, bath) => sum + bath.avg_duration_seconds * bath.dips, 0) / baths.reduce((sum, bath) => sum + bath.dips, 0))(data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('OURO')) ?? []);
  const allRhodiumMeanDuration = ((baths) => baths.reduce((sum, bath) => sum + bath.avg_duration_seconds * bath.dips, 0) / baths.reduce((sum, bath) => sum + bath.dips, 0))(data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('RODIO')) ?? []);
  const allPalladiumMeanDuration = ((baths) => baths.reduce((sum, bath) => sum + bath.avg_duration_seconds * bath.dips, 0) / baths.reduce((sum, bath) => sum + bath.dips, 0))(data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('PALADIO')) ?? []);
  const allGoldDips = data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('OURO'))?.reduce((sum, bath) => sum + bath.dips, 0);
  const allRhodiumDips = data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('RODIO'))?.reduce((sum, bath) => sum + bath.dips, 0);
  const allPalladiumDips = data?.baths_mean?.filter((bath) => findPoolByRealId(bath.pool_id)?.name.includes('PALADIO'))?.reduce((sum, bath) => sum + bath.dips, 0);

  if (isLoading) return <Loader/>;

  return (
    <Panel title="GENERAL DASHBOARD">
      <div className="w-11/12 m-auto py-8 flex flex-col gap-8">
        <div className="flex justify-between items-baseline">
          <h1 className="text-3xl text-perception-gray-400">
            Electroplating line analytics
            <div className="text-lg text-perception-gray-700">
              Historical performance across the jewelry galvanization line.
            </div>
          </h1>

          <div>
            <GroupButton
              selected={selectedRangeIndex}
              buttons={[
                { text: 'Day', onClick: () => setSelectedRangeIndex(0) },
                { text: 'Week', onClick: () => setSelectedRangeIndex(1) },
                { text: 'Past 7 days', onClick: () => setSelectedRangeIndex(2) },
                { text: 'Month', onClick: () => setSelectedRangeIndex(3) },
              ]}
            />
          </div>
        </div>

        <div className="flex gap-4">
          <DiorDataDashboardGeneralDataBlock
            dataName="Total racks processed"
            value={totalRacksProcessed} unitText="racks"
            trend={totalRacksProcessedPrevious > 0 ? Math.floor((totalRacksProcessed - totalRacksProcessedPrevious) / totalRacksProcessedPrevious * 100) : null}
          />
          <DiorDataDashboardGeneralDataBlock
            dataName="Average throughput"
            value={!isNaN(averageThroughput) ? averageThroughput : '-'} unitText="racks / hour"
            trend={averageThroughputPrevious > 0 ? Math.floor((averageThroughput - averageThroughputPrevious) / averageThroughputPrevious * 100) : null}
          />
          <DiorDataDashboardGeneralDataBlock
            dataName="Line occupancy rate"
            value={!isNaN(averageOccupancyPercent) ? averageOccupancyPercent + '%' : '-'} unitText="All baths, time with rack"
            trend={averageOccupancyPercentPrevious > 0 ? Math.floor((averageOccupancyPercent - averageOccupancyPercentPrevious) / averageOccupancyPercentPrevious * 100) : null}
          />
          <DiorDataDashboardGeneralDataBlock
            dataName="Precious metal baths occupancy"
            value={!isNaN(preciousOccupancyPercent) ? preciousOccupancyPercent + '%' : '-'} unitText="Gold, rhodium, palladium"
            trend={preciousOccupancyPercentPrevious > 0 ? Math.floor((preciousOccupancyPercent - preciousOccupancyPercentPrevious) / preciousOccupancyPercentPrevious * 100) : null}
          />
        </div>

        <Panel noHeader rounded category="secondary">
          <h1 className="text-xl text-perception-gray-500">
            Racks per process
            <div className="text-sm text-perception-gray-600">
              Total rack count by bath, ordered by process sequence.
            </div>
          </h1>

          <DiorDataDashboardGraphRacksPerProcess rackData={data?.racks_per_process ?? []} isLoading={isLoading} />
        </Panel>

        <Panel noHeader rounded category="secondary">
          <h1 className="text-xl text-perception-gray-500">
            Dip time overview - All baths
            <div className="text-sm text-perception-gray-600">
              Nominal vs. measured dip time across the full bath list
            </div>
          </h1>

          <div className="h-[400px] max-h-[400px] overflow-y-auto mt-4">
            <SimpleTable
              data={
                data?.baths_mean
                  ?.sort((b1, b2) => parseInt(b1.pool_id) - parseInt(b2.pool_id))
                  ?.map((bath) => ({
                    'Bath name':
                      <div className="flex gap-4">
                        <div className="text-xs bg-perception-gray-700 text-white px-3 py-0.5 rounded-md">
                          {getSchemeId(bath.pool_id)}
                        </div>
                        <div>{findPoolByRealId(bath.pool_id).name}</div>
                      </div>,
                    'Average': formatSecondsToHMS(bath.avg_duration_seconds),
                    'Dips': bath.dips
                  }))
              }
            />
          </div>
        </Panel>

        <h1 className="text-3xl text-perception-gray-400">
          Precious metal bath usage
          <div className="text-lg text-perception-gray-700">
            Racks processed and average dip time for precious metal baths.
          </div>
        </h1>

        <div className="flex gap-4">
          <DiorDataDashboardPreciousDataBlock
            dataName="Gold"
            values={[allGoldDips, formatSecondsToHMS(allGoldMeanDuration, 'short')]}
            unitTexts={['Racks processed', 'Average dip time']}
          />
          <DiorDataDashboardPreciousDataBlock
            dataName="Rhodium"
            values={[allRhodiumDips, formatSecondsToHMS(allRhodiumMeanDuration, 'short')]}
            unitTexts={['Racks processed', 'Average dip time']}
          />
          <DiorDataDashboardPreciousDataBlock
            dataName="Palladium"
            values={[allPalladiumDips, formatSecondsToHMS(allPalladiumMeanDuration, 'short')]}
            unitTexts={['Racks processed', 'Average dip time']}
          />
        </div>

        <Panel noHeader rounded category="secondary" className="max-h-[500px]">
          <h1 className="text-xl text-perception-gray-500">
            Bath maintenance threshold
            <div className="text-sm text-perception-gray-600">
              Cumulative usage time vs editable maintenance threshold
            </div>
          </h1>

          <DiorDataDashboardBathsMaintenance
            baths={baths}
            bathsAreLoading={bathsAreLoading}
            refetchBaths={refetchBaths}
            updateBath={handleUpdateBath}
            bathsData={data?.baths_maintenance}
          />
        </Panel>
      </div>
    </Panel>
  );
};
